import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  OnInit,
  Signal,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DocumentIcon } from '@sportello-balneari/shared-components';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { v4 as uuid } from 'uuid';
import { CompilingApiService } from '../../../api/compiling-api.service';
import { BuilderStatusService } from '../../../services/builder/builder-status.service';
import { CompilingWidgetService } from '../../../services/compiling/widget.service';
import { AnswerPermissionsService } from '../../../services/compiling/state/answer-permissions.service';
import { FileDialogCustomization } from './filedialog-edit';

export interface FileDialogCompilingForm {
  fileName: string;
  blobName?: string;
  url: string;
  id: string;
}

@Component({
  selector: 'lib-filedialog-compiling',
  standalone: true,
  imports: [
    MatIconModule,
    ProgressSpinnerModule,
    TooltipModule,
    MatButtonModule,
    TableModule,
    DocumentIcon,
  ],
  template: `
    @let widget = widgetService.widget();
    @if (widget) {
      <p-table
        class="rounded-xl"
        [value]="totalFiles()"
        (dragover)="$event.preventDefault(); dragIsOverTable.set(true)"
        (dragleave)="$event.preventDefault(); dragIsOverTable.set(false)"
        (drop)="onDrop($event)"
        #dragTr
      >
        <ng-template #header>
          <tr>
            <th class="w-2" [class.bg-blue-50]="dragIsOverTable()">
              <div class="flex items-center justify-between gap-3">
                <span> Documenti</span>
                @if (permissions.canEditTechnicalOffer()) {
                  <input
                    type="file"
                    #fileUpload
                    accept="*"
                    [multiple]="true"
                    hidden
                    (change)="onSend($event, fileUpload)"
                  />
                  <button
                    mat-icon-button
                    (click)="invokeFileupload($event, fileUpload)"
                  >
                    <mat-icon svgIcon="heroicons_outline:paper-clip" />
                  </button>
                }
              </div>
            </th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-item let-index="rowIndex">
          <tr [class.bg-blue-50]="dragIsOverTable()">
            <td>
              <div class="flex flex-1 items-center gap-3">
                <div class="flex items-center gap-3">
                  <lib-document-icon [fileName]="item.fileName" />
                  <span class="flex flex-1 grow font-semibold text-slate-900">{{
                    item.fileName
                  }}</span>
                </div>
                <div class="ml-auto flex">
                  @if (item.blobName) {
                    <button
                      mat-icon-button
                      color="primary"
                      type="button"
                      (click)="download(item.blobName)"
                    >
                      <mat-icon
                        svgIcon="heroicons_outline:arrow-down-tray"
                      ></mat-icon>
                    </button>

                    @if (permissions.canEditTechnicalOffer()) {
                      <button
                        mat-icon-button
                        color="warn"
                        (click)="remove($event, index)"
                      >
                        <mat-icon svgIcon="heroicons_outline:x-mark"></mat-icon>
                      </button>
                    }
                  } @else {
                    <i
                      class="pi pi-spin pi-spinner"
                      style="font-size: 2rem"
                    ></i>
                  }
                </div>
              </div>
            </td>
          </tr>
        </ng-template>
        <ng-template #emptymessage>
          <tr [class.bg-blue-50]="dragIsOverTable()">
            <td colspan="3" class="py-4 text-center">
              <div class="text-sm text-gray-500">
                @if (permissions.canEditTechnicalOffer()) {
                  Nessun file caricato. Clicca sulla graffetta o trascina qui
                  un file per eseguire l'upload
                } @else {
                  Nessun file caricato
                }
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    }
  `,
  styleUrls: ['../style.css'],
})
export class FileDialogCompilingComponent implements OnInit {
  private readonly injector = inject(Injector);
  private readonly compilingApiService = inject(CompilingApiService);
  protected readonly widgetService: CompilingWidgetService<
    FileDialogCustomization,
    FileDialogCompilingForm[]
  > = inject(
    CompilingWidgetService<FileDialogCustomization, FileDialogCompilingForm[]>,
  );

  protected readonly builderStatusService = inject(BuilderStatusService, {
    optional: true,
  });
  protected readonly permissions = inject(AnswerPermissionsService);

  dragIsOverTable = signal(false);

  hasUploaded = computed(() => {
    const value = this._signalValueChange?.();
    const initialValue = this.widgetService.formControl()?.value;

    if (initialValue?.length != 0) return true;

    if (!value) return undefined;

    return value.length > 0;
  });

  protected uploadingFiles = signal<{ id: string; fileName: string }[]>([]);
  private _signalValueChange:
    | Signal<FileDialogCompilingForm[] | undefined>
    | undefined = undefined;

  isUploading = signal(false);

  totalFiles = computed(() => {
    const uploadingFiles = this.uploadingFiles();
    const controlValue = this._signalValueChange?.();

    if (controlValue === undefined)
      return this.widgetService.formControl()?.value ?? [];

    return [...(controlValue ?? []), ...(uploadingFiles ?? [])];
  });

  constructor() {
    effect(() => {
      const control = this.widgetService.formControl();
      if (!control) return;

      if (typeof control.value === 'string') control.setValue([]);
    });
  }

  ngOnInit(): void {
    const control = this.widgetService.formControl();
    if (!control) return;

    this._signalValueChange = toSignal(control.valueChanges, {
      injector: this.injector,
    });
  }

  onSend(event: Event, element: HTMLInputElement) {
    const files = (event.target as HTMLInputElement).files;
    if (!files || files.length === 0) return;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      this.uploadFile(file);
    }
    element.value = '';
  }

  remove(event: Event, index: number) {
    event.preventDefault();
    event.stopPropagation();
    const control = this.widgetService.formControl()?.value as
      | FileDialogCompilingForm[]
      | undefined;
    if (!control) return;
    this.widgetService
      .formControl()
      ?.patchValue([...control.filter((f, i) => i !== index)]);
  }

  onDrop(evt: DragEvent) {
    evt.preventDefault();
    evt.stopPropagation();

    this.dragIsOverTable.set(false);

    if (!this.permissions.canEditTechnicalOffer()) return;

    const files = evt.dataTransfer?.files;
    if (!files) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      this.uploadFile(file);
    }
  }

  invokeFileupload(event: PointerEvent, fileUpload: HTMLInputElement) {
    //console.log('CAIO');
    event.preventDefault();
    event.stopPropagation();
    fileUpload.click();
  }

  private uploadFile(file: File) {
    // Only upload to server if we're not in builder mode, otherwise just create a fake URL for preview purposes
    if (!this.builderStatusService) {
      const id = uuid();
      const obj: { id: string; fileName: string } = {
        id,
        fileName: file.name,
      };
      this.uploadingFiles.set([...this.uploadingFiles(), obj]);

      this.compilingApiService.uploadFile({ attachment: file }).subscribe({
        next: (response) => {
          const currentValue = this.widgetService.formControl()?.value;
          if (!currentValue) return;

          const newValue: FileDialogCompilingForm = {
            id: crypto.randomUUID(),
            fileName: file.name,
            url: response.downloadUrl,
            blobName: response.blobName,
          };
          this.widgetService
            .formControl()
            ?.patchValue([...currentValue, newValue]);
        },
        complete: () => {
          this.uploadingFiles.set(
            this.uploadingFiles().filter((f) => f.id !== id),
          );
        },
      });
    } else {
      const control = this.widgetService.formControl()?.value;
      if (!control) return;

      this.widgetService.formControl()?.patchValue([
        ...control,
        {
          id: uuid(),
          fileName: file.name,
          url: URL.createObjectURL(file),
          blobName: file.name,
        },
      ]);
    }
  }

  download(blobName: string) {
    // Disable download if we're in builder mode, since the file is not actually uploaded
    if (this.builderStatusService) return;

    this.compilingApiService.getDownloadLink(blobName).subscribe((response) => {
      const link = document.createElement('a');
      link.href = response.downloadUrl;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  }
}
